import { client } from "@/modules/integrations/accounts/client";
import { isRecord } from "@/v1/utils/record";
import type { CustomerCreate } from "@/providers/smarta-accounts/accounts-api-client";
import { Lead, LeadPage } from "./types";

export const LEADS_PAGE_SIZE = 20;

const HTTP_CONFLICT = 409;

/** The fields the New lead form sends. The status is always ONBOARDING. */
export type LeadCreate = Omit<CustomerCreate, "status">;

/**
 * The email cannot be used for this lead. On `POST /api/customers` a 409
 * means the email belongs to an operator, or to the main taxpayer of another
 * customer. The message is the API's own, because only the API knows which
 * one, and it names that other customer's number. The form shows it at the
 * email field. It is empty when the API sent no message.
 */
export class LeadEmailTakenError extends Error {
  name = "LeadEmailTakenError";
}

const errorMessageOf = (error: unknown): string => {
  const details = isRecord(error) && isRecord(error.error) ? error.error : {};
  return typeof details.message === "string" ? details.message : "";
};

/**
 * One page of leads from `GET /api/customers?status=ONBOARDING`, paged on the
 * server with `limit` / `offset` (a true item offset, not a page number).
 * Archived leads stay out, as they did on the old `/api/leads` list.
 */
export const listLeads = async (
  page: number,
  pageSize = LEADS_PAGE_SIZE
): Promise<LeadPage> => {
  const { data, error } = await client.GET("/api/customers", {
    params: {
      query: {
        status: "ONBOARDING",
        archived: "false",
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed list-leads response");
  }
  return { leads: data.data, total: data.pagination.total };
};

/**
 * Create a lead via `POST /api/customers`: a customer with status ONBOARDING
 * and a main taxpayer. A 409 becomes a `LeadEmailTakenError` with the API's
 * message; every other failure is thrown as is.
 */
export const createLead = async (payload: LeadCreate): Promise<Lead> => {
  const { data, error, response } = await client.POST("/api/customers", {
    body: { ...payload, status: "ONBOARDING" },
  });
  if (response.status === HTTP_CONFLICT) {
    throw new LeadEmailTakenError(errorMessageOf(error));
  }
  if (error || !data?.data) {
    throw error || new Error("Malformed create-lead response");
  }
  return data.data;
};

/** Assign an operator via `PUT /api/customers/{id}/manager`. */
export const setLeadAssignee = async (
  id: string,
  operatorId: string
): Promise<Lead> => {
  const { data, error } = await client.PUT("/api/customers/{id}/manager", {
    params: { path: { id } },
    body: { operatorId },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed set-lead-assignee response");
  }
  return data.data;
};

/** Remove the assignee via `DELETE /api/customers/{id}/manager`. */
export const clearLeadAssignee = async (id: string): Promise<Lead> => {
  const { data, error } = await client.DELETE("/api/customers/{id}/manager", {
    params: { path: { id } },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed clear-lead-assignee response");
  }
  return data.data;
};
